import { Spacing } from '@persona-id/design-tokens';
import { UserIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import { AttributeItemProps } from 'dashboard/components/AttributeItem/AttributeItem';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import { formatAddress } from 'dashboard/lib/utils/formatters';
import {
  AccountOwnerResult,
  ReportMxAccount,
} from 'dashboard/models/report/integration/mx-account';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import MXIcon from 'images/icons/marketplace/MXIcon.svg';

const AccountOwnerContainer = styled('div')`
  padding: 0px ${Spacing[3]};
  width: 100%;
`;

const AccountOwnerHeader = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const LabelSticker = styled(Sticker)`
  margin-left: 10px;
`;

const TableHeaderContainer = styled('div')`
  display: flex;
  padding: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${BASE}px;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${Spacing[2]};
`;

const TableHeader = styled('div')`
  margin-left: ${Spacing[3]};
  display: flex;
  justify-content: space-between;
  font-weight: ${FontWeight.Bold};
`;

enum MxAccountInfoAttribute {
  AccountGuid = 'account-guid',
  Address = 'address',
  Email = 'email',
  Name = 'owner-name',
  Guid = 'guid',
  MemberGuid = 'member-guid',
  Phone = 'phone',
  UserGuid = 'user-guid',
}

const IDENTITY_INFO_LAYOUT = [
  MxAccountInfoAttribute.Name,
  MxAccountInfoAttribute.Phone,
  MxAccountInfoAttribute.Email,
  MxAccountInfoAttribute.Address,
];

const MX_ACCOUNT_INFO_LAYOUT = [
  MxAccountInfoAttribute.AccountGuid,
  MxAccountInfoAttribute.Guid,
  MxAccountInfoAttribute.MemberGuid,
  MxAccountInfoAttribute.UserGuid,
];

interface MxAccountProps {
  report: ReportMxAccount;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const MxAccount = ({ report, isRedacted, multiCardFormat = false }: MxAccountProps) => {
  const accountOwners = report?.attributes?.result?.['account-owner-results'] || [];

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <>
        {multiCardFormat && <Card.Title>{`Account Owners (${accountOwners.length})`}</Card.Title>}

        {isEmpty(accountOwners) ? (
          <>
            <Spacer height={2} />
            <span>No records found.</span>
          </>
        ) : (
          <>
            <TableHeaderContainer>
              <TableHeader>Name</TableHeader>
            </TableHeaderContainer>

            {accountOwners.map((accountOwner, index) => (
              <AccountOwnerContainer key={index}>
                <AccountOwner accountOwner={accountOwner} isRedacted={isRedacted} index={index} />
              </AccountOwnerContainer>
            ))}
          </>
        )}
      </>
    </ReportResultLayout>
  );
};

interface AccountOwnerProps {
  accountOwner: AccountOwnerResult;
  isRedacted: boolean;
  index: number;
}

const AccountOwner = ({
  accountOwner: {
    'account-guid': accountGuid,
    address: address,
    city: city,
    country: country,
    email: emailAddress,
    guid: guid,
    'member-guid': memberGuid,
    'owner-name': name,
    phone: phoneNumber,
    'postal-code': postalCode,
    state: state,
    'user-guid': userGuid,
    'is-match': isMatch,
  },
  isRedacted,
  index,
}: AccountOwnerProps) => {
  const identityAttributeMap = {
    [MxAccountInfoAttribute.Name]: {
      title: 'Full Names',
      value: name,
      placeholder: '-',
    },
    [MxAccountInfoAttribute.Phone]: {
      title: 'Phone Number',
      value: phoneNumber,
      placeholder: '-',
    },
    [MxAccountInfoAttribute.Email]: {
      title: 'E-mail',
      value: emailAddress,
      placeholder: '-',
    },
    [MxAccountInfoAttribute.Address]: {
      title: 'Address',
      value: formatAddress({
        addressStreet1: address,
        addressCity: city,
        addressSubdivision: state,
        addressPostalCode: postalCode,
        countryCode: country,
      }),
      placeholder: '-',
    },
  } as { [key in MxAccountInfoAttribute]?: AttributeItemProps };

  const mxAccountAttributeMap = {
    [MxAccountInfoAttribute.Guid]: {
      title: 'Guid',
      value: guid,
      placeholder: '-',
    },
    [MxAccountInfoAttribute.AccountGuid]: {
      title: 'Account Guid',
      value: accountGuid,
      placeholder: '-',
    },
    [MxAccountInfoAttribute.UserGuid]: {
      title: 'User Guid',
      value: userGuid,
      placeholder: '-',
    },
    [MxAccountInfoAttribute.MemberGuid]: {
      title: 'Member Guid',
      value: memberGuid,
      placeholder: '-',
    },
  } as { [key in MxAccountInfoAttribute]?: AttributeItemProps };

  return (
    <Card.CollapsibleSection
      defaultExpanded={index === 0}
      title={
        <AccountOwnerHeader>
          {name}
          {isMatch && <LabelSticker variant='success'>Match</LabelSticker>}
        </AccountOwnerHeader>
      }
    >
      <Card.Title icon={<UserIcon />}>Identity Details</Card.Title>
      <Spacer height={2} />
      <AttributeGrid>
        {IDENTITY_INFO_LAYOUT.map((attribute) =>
          generateAttributeItem(identityAttributeMap[attribute], attribute, isRedacted),
        ).filter(Boolean)}
      </AttributeGrid>
      <Spacer height={4} />
      <Card.Title icon={<MXIcon />}>MX Details</Card.Title>
      <Spacer height={2} />
      <AttributeGrid colCount={2}>
        {MX_ACCOUNT_INFO_LAYOUT.map((attribute) =>
          generateAttributeItem(mxAccountAttributeMap[attribute], attribute, isRedacted),
        ).filter(Boolean)}
      </AttributeGrid>
      <Spacer height={2} />
    </Card.CollapsibleSection>
  );
};

const generateAttributeItem = (
  attributeItemProps: AttributeItemProps | undefined,
  key: MxAccountInfoAttribute,
  isRedacted: boolean,
) => {
  if (!attributeItemProps) {
    return <></>;
  }

  const component = (
    <AttributeItem
      {...attributeItemProps}
      key={key}
      dataTest={`report-mx-account__${key}`}
      isRedacted={isRedacted}
    />
  );

  return component;
};

export default MxAccount;
